import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Give me a quick ops brief for today",
  "How many beds are vacant?",
  "Who has outstanding dues?",
  "What are the open complaints and their priorities?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi — I'm your Param Hostel copilot. Ask me anything about occupancy, dues, complaints or mess." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err));
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I hit an error. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-accent/10 text-accent flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <div className="eyebrow text-muted-foreground">Copilot</div>
          <h1 className="font-display text-4xl font-bold mt-1">AI Assistant</h1>
        </div>
      </div>

      <Card className="border-border bg-white overflow-hidden flex flex-col" style={{ height: "calc(100vh - 260px)", minHeight: 420 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] rounded-lg px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground border border-border"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-secondary text-secondary-foreground rounded-lg px-4 py-3 text-sm border border-border flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="px-6 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs px-3 py-2 rounded-full border border-border hover:bg-secondary transition-colors"
                data-testid={`suggestion-${s.slice(0, 8).replace(/\s/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-border p-4 flex gap-2 bg-background"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the copilot..."
            disabled={loading}
            data-testid="chat-input"
          />
          <Button type="submit" disabled={loading || !input.trim()} data-testid="chat-send">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </Card>
    </div>
  );
}
